
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Grid, Button, Table, TableBody, TableCell, TableHead, TableRow,
    TableContainer, Paper, IconButton, Menu, MenuItem, Popover,
    Stack, Chip, Pagination, FormControl, InputLabel, Select,
    Box, Tooltip, Typography
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import InfoIcon from '@mui/icons-material/Info';
import useStyles from "../../assets/styles/SupMangComp.style";
import API from '../../utils/Axios.utils';
import useDebounce from '../../utils/useDebounce';
import CustomModel from '../../utils/CustomModel';
import AdminStatusUpdateForm from './AdminStatusUpdateForm';
import { ResumeScoreChip, ResumeScoreFilters } from '../common/ResumeScore';
import { Notification } from "../../utils/Notification";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import useAuth from "../../hooks/useAuth";
import { format } from "date-fns";
import { useDeleteCandidate } from '../../hooks/useCandidateManagement';
import { parseStatusLabel } from '../../utils/statusUtils';

const SupplierCandidateList = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    const { token, role } = useAuth();
    const isAdmin = role?.toLowerCase() === 'admin';
    const config = React.useMemo(() => ({
        headers: { Authorization: `Bearer ${token}` }
    }), [token]);

    const location = useLocation();
    const jobCode = location.state?.jobCode;

    // Pagination
    const [listCount, setListCount] = useState(0);
    const [page, setPage] = useState(1);

    // API data
    const [candidates, setCandidates] = useState([]);
    const [client, setClient] = useState();
    const [recruiterName, setRecruiterName] = useState("");

    // ✅ UPDATED: Use user_id instead of supplier_id
    const [selectedUserIds, setSelectedUserIds] = useState([]); // UI only - user_id
    const [appliedUserIds, setAppliedUserIds] = useState([]);   // API only - user_id

    const [supplierData, setSupplierData] = useState([]);
    const [showLoader, setShowLoader] = useState(false);
    const [searchText, setSearchText] = React.useState('');

    // Resume score filter + sort, sent to the API as query params
    const [scoreFilters, setScoreFilters] = useState({
        selectionStatus: '',
        minScore: '',
        sortOrder: '',
    });

    const handleScoreFilterChange = (change) => {
        setScoreFilters((prev) => ({ ...prev, ...change }));
        setPage(1);   // a narrower result set invalidates the current page number
    };

    // Notification state
    const [alert, setAlert] = useState(false);
    const [alertContent, setAlertContent] = useState("");
    const [alertType, setAlertType] = useState("");

    // Status Update Modal state
    const [statusUpdateOpen, setStatusUpdateOpen] = useState(false);
    const [selectedCandidate, setSelectedCandidate] = useState(null);
    const [update, setUpdate] = useState(0);

    // Delete modal state
    const [delOpen, setDelOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);

    // More Menu state
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuRow, setMenuRow] = useState(null);
    const open = Boolean(anchorEl);

    // Info Popover state
    const [infoAnchorEl, setInfoAnchorEl] = useState(null);
    const [infoRow, setInfoRow] = useState(null);

    // Delete mutation
    const { mutate: deleteCandidate } = useDeleteCandidate();

    const handleAlert = () => {
        setAlert(false);
    };

    // ✅ UPDATED: getProfiles uses appliedUserIds with user_id param
    const getProfiles = useCallback(async () => {
        if (!jobCode) {
            console.error('No jobCode found in location.state');
            return;
        }

        try {
            let url = `/api/profile/supplier_candidate/job/${jobCode}?page=${page}&pageSize=10`;

            // ✅ CHANGED: Use user_id parameter (not supplierid)
            if (appliedUserIds.length > 0) {
                url += `&userid=${appliedUserIds.join(',')}`; // ✅ userid param
            }

            // Resume score filter/sort — applied in SQL so they span every candidate
            // on the job, not just the page currently loaded
            if (scoreFilters.selectionStatus) {
                url += `&selectionStatus=${scoreFilters.selectionStatus}`;
            }
            if (scoreFilters.minScore) {
                url += `&minScore=${scoreFilters.minScore}`;
            }
            if (scoreFilters.sortOrder) {
                url += `&sortBy=resume_score&sortOrder=${scoreFilters.sortOrder}`;
            }

            console.log('🔍 API URL:', url);
            console.log('🔍 Applied User IDs:', appliedUserIds);

            const res = await API.get(url, config);
            setCandidates(res?.data?.candidates || []);
            setListCount(res?.data?.totalCount || 0);
            setClient(res?.data?.client?.name || "-");
            // Property of the job this screen is scoped to, not of any candidate.
            setRecruiterName(res?.data?.recruiter_name || "");
        } catch (error) {
            console.error('Error fetching candidates:', error);
        }
    }, [page, jobCode, appliedUserIds, update, scoreFilters]); // ✅ appliedUserIds

    const handlePageChange = (e, value) => {
        setPage(value);
    };

    // Supplier filter handlers
    const debouncedQueryList = useDebounce(searchText, 500);

    const getSupplier = useCallback(async () => {
        setShowLoader(true);
        let url = 'api/users/getallsupplier';
        if (searchText) url += `?search=${debouncedQueryList}`;

        try {
            const res = await API.get(url, config);
            setShowLoader(false);
            setSupplierData(res?.data?.supplier || []);
        } catch (error) {
            setShowLoader(false);
            console.error('Error fetching suppliers:', error);
        }
    }, [config, searchText, debouncedQueryList]);

    useEffect(() => {
        getSupplier();
    }, [getSupplier]);

    // ✅ UPDATED: handleSupplierChange uses user_id
    const handleSupplierChange = (event) => {
        const selectedValues = event.target.value || [];

        if (selectedValues.includes('all') || selectedValues.some(val => val === 'all')) {
            // ✅ CHANGED: Use user_id instead of supplier_id
            const allUserIds = supplierData.map(supplier => supplier.user_id);
            setSelectedUserIds(allUserIds.map(id => id.toString()));
        } else {
            setSelectedUserIds(selectedValues);
        }
    };

    const handleSubmit = () => {
        setAppliedUserIds(selectedUserIds); // UI → Applied
        setPage(1);
        console.log('✅ Applied user_ids:', selectedUserIds);
    };

    const handleClearFilters = () => {
        setSelectedUserIds([]);     // UI state
        setAppliedUserIds([]);      // API state
        setPage(1);
        console.log('🧹 Cleared all filters');
    };

    // More Menu handlers
    const handleMenuOpen = (event, row) => {
        setAnchorEl(event.currentTarget);
        setMenuRow(row);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuRow(null);
    };

    const handleStatusUpdateDialog = () => {
        setStatusUpdateOpen(!statusUpdateOpen);
    };

    const handleStatusUpdateOpen = (item) => {
        setSelectedCandidate(item);
        setStatusUpdateOpen(true);
    };

    const handleStatusUpdateClose = () => {
        setStatusUpdateOpen(false);
        setSelectedCandidate(null);
    };

    const handleDeleteOpen = (row) => {
        setDeleteTarget(row);
        setDelOpen(true);
        handleMenuClose();
    };

    const handleDeleteClose = () => {
        setDelOpen(false);
        setDeleteTarget(null);
    };

    const handleDeleteConfirm = () => {
        if (!deleteTarget) return;
        deleteCandidate(deleteTarget.profile_id, {
            onSuccess: (data) => {
                handleDeleteClose();
                setAlertContent(data?.message || 'Candidate deleted successfully');
                setAlertType('success');
                setAlert(true);
                // Removing the only row on a trailing page would leave the table
                // empty; step back a page instead, which refetches on its own.
                if (candidates.length === 1 && page > 1) {
                    setPage((prev) => prev - 1);
                } else {
                    setUpdate((prev) => prev + 1);
                }
            },
            onError: (error) => {
                setAlertContent(error?.response?.data?.message || 'Error deleting candidate');
                setAlertType('error');
                setAlert(true);
            },
        });
    };

    const extractFileNameFromUrl = (url) => {
        if (!url) return 'file';
        const parts = url.split('/');
        const lastPart = parts[parts.length - 1];
        return decodeURIComponent(lastPart.split('?')[0]);
    };

    const handleDownload = useCallback((fileUrl) => {
        const fileName = extractFileNameFromUrl(fileUrl);
        const fileExt = fileName.split('.').pop()?.toLowerCase();

        if (fileExt === 'pdf' || fileExt === 'doc' || fileExt === 'docx') {
            fetch(fileUrl)
                .then(r => r.blob())
                .then(blob => {
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = fileName;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                })
                .catch(error => {
                    console.error('Blob download failed:', error);
                    const a = document.createElement('a');
                    a.href = fileUrl;
                    a.download = fileName;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                });
        } else {
            const a = document.createElement('a');
            a.href = fileUrl;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        }
    }, []);

    useEffect(() => {
        getProfiles();
    }, [getProfiles]);

    const getRowNumber = (index) => {
        return (page - 1) * 10 + index + 1;
    };

    const navigateToPreviousPage = () => {
        const from = location.state?.from;
        if (from) {
            navigate(from, { replace: true });
        } else {
            navigate(-1);
        }
    };

    // ✅ UPDATED: renderSelectedSuppliers uses user_id lookup
    const renderSelectedSuppliers = (selected) => {
        if (selected.length === 0) {
            return 'All Suppliers';
        }

        const selectedSuppliers = selected.map(id => {
            // ✅ CHANGED: Find by user_id instead of supplier_id
            const supplier = supplierData.find(s => s.user_id === parseInt(id));
            return supplier ? `${supplier.supplier_org_name} (${supplier.supplier_code})` : id;
        }).join(', ');

        return selectedSuppliers.length > 30
            ? `${selectedSuppliers.substring(0, 30)}...`
            : selectedSuppliers;
    };


    return (
        <Grid container className={classes.root}>
            <Grid item xs={12} className={classes.headerContainer}>
                <div style={{ marginTop: "20px" }}>
                    <Button startIcon={<ArrowBackIcon />} onClick={navigateToPreviousPage}>
                        Back
                    </Button>
                </div>
            </Grid>

            <Grid item xs={12} className={classes.headerContainer}>
                <Grid container alignItems="center" justifyContent="space-between" spacing={2}>
                    <Grid item>
                        <h2 className={classes.pageTitle}>Candidates</h2>
                    </Grid>
                    <Grid item>
                        <Button
                            variant="contained"
                            startIcon={<PersonAddIcon />}
                            onClick={() =>
                                navigate('/candidate_data/addCandidate', {
                                    state: {
                                        jobCode: jobCode,
                                        prefilledJobCode: jobCode,
                                        from: location.pathname + location.search
                                    },
                                })
                            }
                            style={{ background: '#2777AA', textTransform: 'none' }}
                        >
                            + Add Candidate
                        </Button>
                    </Grid>
                </Grid>
            </Grid>

            <Grid container spacing={0} sx={{ alignItems: 'flex-end', mb: 3 }}>
                <Grid item xs={12} md={8}>
                    <FormControl fullWidth size="small">
                        <InputLabel id="supplier-label" shrink>Select Suppliers</InputLabel>
                        <Select
                            labelId="supplier-label"
                            multiple
                            value={selectedUserIds}  // ✅ user_ids
                            label="Select Suppliers"
                            onChange={handleSupplierChange}
                            displayEmpty
                            renderValue={renderSelectedSuppliers}
                        >
                            <MenuItem value="all">
                                All Suppliers
                            </MenuItem>
                            {supplierData.map((supplier) => (
                                <MenuItem
                                    key={supplier?.user_id}      // ✅ Use user_id as key
                                    value={supplier?.user_id}    // ✅ value = user_id (61, 28)
                                >
                                    {`${supplier?.supplier_org_name} (${supplier?.supplier_code})`}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSubmit}
                            size="small"
                        >
                            Apply
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handleClearFilters}
                        >
                            Clear
                        </Button>
                    </Stack>
                </Grid>
            </Grid>

            <Grid item xs={12} sx={{ px: 2, pb: 1 }}>
                <Stack direction="row" spacing={2} sx={{ justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    <ResumeScoreFilters
                        selectionStatus={scoreFilters.selectionStatus}
                        minScore={scoreFilters.minScore}
                        sortOrder={scoreFilters.sortOrder}
                        onChange={handleScoreFilterChange}
                    />
                </Stack>
            </Grid>

            {/* Table remains exactly the same */}
            <Grid item xs={12} className={classes.tableGrid}>
                <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 650 }} size="small" aria-label="a dense table">
                        <TableHead>
                            <TableRow className={classes.tableHeadRow}>
                                <TableCell className={classes.tableHeadCell}>Sr.no</TableCell>
                                <TableCell className={classes.tableHeadCell}>Candidate Name</TableCell>
                                <TableCell className={classes.tableHeadCell}>Resume</TableCell>
                                <TableCell className={classes.tableHeadCell}>Client</TableCell>
                                <TableCell className={classes.tableHeadCell}>Supplier</TableCell>
                                <TableCell className={classes.tableHeadCell}>Recruiter</TableCell>
                                <TableCell className={classes.tableHeadCell}>Resume Score</TableCell>
                                <TableCell className={classes.tableHeadCell}>Status</TableCell>
                                <TableCell className={classes.tableHeadCell}>Status Update Date</TableCell>
                                <TableCell align="right" className={classes.tableHeadCell}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody className={classes.noHorizontalBorder}>
                            {candidates.map((row, index) => {
                                const rowId = row.profile_id || row.profileid;
                                const rowNumber = getRowNumber(index);

                                return (
                                    <TableRow key={rowId} hover>
                                        <TableCell className={classes.tableBodyCell} sx={{ fontWeight: 'bold', width: 50 }}>
                                            {rowNumber}
                                        </TableCell>
                                        <TableCell className={classes.tableBodyCell}>{row.candidate_name}</TableCell>
                                        <TableCell className={classes.tableBodyCell}>
                                            {(row.pipeline_type === 'non_resume_based' || !row.url) ? 'NA' : extractFileNameFromUrl(row.url)}
                                        </TableCell>
                                        <TableCell className={classes.tableBodyCell}>{client}</TableCell>
                                        <TableCell className={classes.tableBodyCell}>{row.supplier_org_name}</TableCell>
                                        {/* The job's recruiter. This screen is scoped to a single
                                            job, so the value is the same on every row. */}
                                        <TableCell className={classes.tableBodyCell}>{recruiterName || "-"}</TableCell>
                                        <TableCell className={classes.tableBodyCell}>
                                            <ResumeScoreChip
                                                score={row.resume_score}
                                                status={row.resume_selection_status}
                                                reason={row.resume_selection_reason}
                                                explanationHtml={row.resume_explanation_html}
                                            />
                                        </TableCell>
                                        <TableCell className={classes.tableBodyCell}>
                                            <Tooltip
                                                title={parseStatusLabel(row?.detailed_status) || 'No detailed status'}
                                                arrow
                                                placement="top"
                                            >
                                                <Chip label={parseStatusLabel(row?.detailed_status)} size="small" color="default" />
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell className={classes.tableBodyCell}>
                                            {row.status_date ? format(new Date(row.status_date), "dd-MM-yyyy") : "-"}
                                        </TableCell>
                                        <TableCell align="left">
                                            <IconButton
                                                size="small"
                                                onClick={(e) => handleMenuOpen(e, row)}
                                                sx={{ color: "#2777AA" }}
                                            >
                                                <MoreVertIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {candidates.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={9} align="center">
                                        No candidates found for applied filters.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
                    <Pagination
                        classes={{ root: classes.pagination }}
                        count={Math.ceil(listCount / 10)}
                        page={page}
                        onChange={handlePageChange}
                        showFirstButton
                        showLastButton
                    />
                </Box>
            </Grid>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleMenuClose}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            >
                <MenuItem
                    disabled={menuRow?.pipeline_type === 'non_resume_based' || menuRow?.JobPost?.pipeline_type === 'non_resume_based' || menuRow?.Job?.pipeline_type === 'non_resume_based' || !menuRow?.url}
                    onClick={() => {
                        navigate("/admindashboard/viewFile", {
                            state: {
                                fileUrl: menuRow?.url,
                                from: location.pathname + location.search,
                                jobCode: jobCode
                            }
                        });
                        handleMenuClose();
                    }}
                >
                    <VisibilityIcon sx={{ mr: 1, color: (menuRow?.pipeline_type === 'non_resume_based' || menuRow?.JobPost?.pipeline_type === 'non_resume_based' || menuRow?.Job?.pipeline_type === 'non_resume_based' || !menuRow?.url) ? 'action.disabled' : '#2777AA' }} />
                    View
                </MenuItem>
                <MenuItem
                    disabled={menuRow?.pipeline_type === 'non_resume_based' || menuRow?.JobPost?.pipeline_type === 'non_resume_based' || menuRow?.Job?.pipeline_type === 'non_resume_based' || !menuRow?.url}
                    onClick={() => {
                        handleDownload(menuRow?.url);
                        handleMenuClose();
                    }}
                >
                    <DownloadIcon sx={{ mr: 1, color: (menuRow?.pipeline_type === 'non_resume_based' || menuRow?.JobPost?.pipeline_type === 'non_resume_based' || menuRow?.Job?.pipeline_type === 'non_resume_based' || !menuRow?.url) ? 'action.disabled' : '#2777AA' }} />
                    Download
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        navigate("/candidate_data/editcandidate", {
                            state: { CandidateDetails: menuRow, from: location.pathname + location.search },
                        });
                        handleMenuClose();
                    }}
                >
                    <EditIcon sx={{ mr: 1, color: '#2777AA' }} />
                    Edit Candidate
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        handleStatusUpdateOpen(menuRow);
                        handleMenuClose();
                    }}
                >
                    <EditIcon sx={{ mr: 1, color: '#2777AA' }} />
                    Update
                </MenuItem>
                <MenuItem
                    onClick={(e) => {
                        setInfoRow(menuRow);
                        setInfoAnchorEl(e.currentTarget);
                        handleMenuClose();
                    }}
                >
                    <InfoIcon sx={{ mr: 1, color: '#2777AA' }} />
                    Info
                </MenuItem>
                {/* Soft delete: flags the profile inactive server-side, the row then
                    falls out of this list on the next fetch. */}
                <MenuItem
                    onClick={() => handleDeleteOpen(menuRow)}
                    sx={{ color: '#d32f2f' }}
                >
                    <DeleteIcon sx={{ mr: 1, color: '#d32f2f' }} />
                    Delete Candidate
                </MenuItem>
            </Menu>

            {/* Candidate Details Info Popover */}
            <Popover
                open={Boolean(infoAnchorEl)}
                anchorEl={infoAnchorEl}
                onClose={() => setInfoAnchorEl(null)}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
            >
                <Box sx={{ p: 2, minWidth: 260, maxWidth: 360 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Date:</strong> {infoRow?.updatedAt ? format(new Date(infoRow.updatedAt), "yyyy-MM-dd") : (infoRow?.status_date ? format(new Date(infoRow.status_date), "yyyy-MM-dd") : "-")}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Status:</strong> {parseStatusLabel(infoRow?.detailed_status) || "Update Soon"}
                    </Typography>
                    {infoRow?.status_group === 'Interview_Scheduled' && (
                        <>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>Interview Scheduled Date:</strong> {infoRow?.interview_datetime || "Update Soon"}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>Interview Link:</strong> {infoRow?.interview_link ? (
                                    <a
                                        href={infoRow.interview_link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: "#2777AA", textDecoration: "underline", wordBreak: "break-all" }}
                                    >
                                        {infoRow.interview_link}
                                    </a>
                                ) : "Update Soon"}
                            </Typography>
                        </>
                    )}
                    {infoRow?.profile_feedback && (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Profile Feedback:</strong> {infoRow.profile_feedback}
                        </Typography>
                    )}
                    {infoRow?.interview_feedback && (
                        <Typography variant="body2">
                            <strong>Interview Feedback:</strong> {infoRow.interview_feedback}
                        </Typography>
                    )}
                </Box>
            </Popover>

            {/* Delete Confirmation Dialog */}
            <CustomModel
                handleDialog={handleDeleteClose}
                open={delOpen}
                title="Delete Candidate"
            >
                <Grid container direction="column" spacing={2} sx={{ p: 2 }}>
                    <Grid item>
                        <Typography align="center" variant="body1">
                            Are you sure you want to delete <strong>{deleteTarget?.candidate_name}</strong>?
                        </Typography>
                    </Grid>
                    <Grid item sx={{ display: 'flex', justifyContent: 'center', gap: '10px', mt: 1 }}>
                        <Button
                            variant="contained"
                            onClick={handleDeleteConfirm}
                            style={{ background: '#d32f2f', textTransform: 'none' }}
                        >
                            Yes, Delete
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={handleDeleteClose}
                            style={{ textTransform: 'none' }}
                        >
                            Cancel
                        </Button>
                    </Grid>
                </Grid>
            </CustomModel>

            <CustomModel
                handleDialog={handleStatusUpdateDialog}
                open={statusUpdateOpen}
                title="Status Update"
            >
                <AdminStatusUpdateForm
                    setAlert={setAlert}
                    setAlertType={setAlertType}
                    setAlertContent={setAlertContent}
                    item={selectedCandidate}
                    jobCode={jobCode}
                    handleDialog={handleStatusUpdateClose}
                    setUpdate={setUpdate}
                    update={update}
                />
            </CustomModel>

            <Notification
                message={alertContent}
                open={alert}
                severity={alertType}
                onClose={handleAlert}
            />
        </Grid>
    );
};

export default SupplierCandidateList;
